<template>
    <div class="container">
        <br /><br />
        <div class="jumbotron">
            <h1 class="display-4">Share {{resourceName}}</h1>
            <p class="lead">You are about to share <strong>{{resourceName}}</strong> owned by <strong>{{organizationName}}</strong> data in TransparentChoice application</p>
            <br />

            <table class="table table-sm">
                <thead>
                    <tr>
                        <th v-for="p in dataShareEmailsProviders" :key="p.Name" scope="col">
                            <div class="form-check form-check-inline">
                                <input v-model="emailsProviderName" class="form-check-input" type="radio" :id="p.Name" :name="p.Name" :value="p.Name">
                                <label class="form-check-label" :for="p.Name">{{p.Title}}</label>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td v-for="p in dataShareEmailsProviders" :key="p.Name">{{p.Description}}</td>
                    </tr>
                </tbody>
            </table>

            <div>
                <a v-show="groups.length>0" data-toggle="collapse" href="#groupsCollapse" role="button">Select groups</a>&nbsp;
                <a v-show="emails.length>0" data-toggle="collapse" href="#emailsCollapse" role="button">Select people</a>
            </div>
            <div class="collapse" id="groupsCollapse">
                <div class="card card-body">
                    <div v-for="g in groups" :key="g.Name" class="form-check form-check-inline">
                        <input v-model="emailsProviderGroups" class="form-check-input" type="checkbox" :id="`g_${g.Name}`" :value="g.Name">
                        <label class="form-check-label" :for="`g_${g.Name}`">{{g.Name}}</label>
                    </div>
                </div>
            </div>
            <div class="collapse" id="emailsCollapse">
                <div class="card card-body">
                    <div v-for="e in emails" :key="e.Name" class="form-check form-check-inline">
                        <input v-model="emailsProviderGroups" class="form-check-input" type="checkbox" :id="`e_${e.Name}`" :value="e.Name">
                        <span v-if="e.IsUser" class="fa fa-user text-success" title="User"></span>
                        <span v-else class="fa-regular fa-user text-secondary" title="Not an user"></span>
                        &nbsp;
                        <label class="form-check-label" :for="`e_${e.Name}`" :value="e">{{e.Name}}</label>
                    </div>
                </div>
            </div>
            <ValidationError context="EmailsProviderName" :errors="validationErrors"></ValidationError>
            <hr />

            <div class="row">
                <div v-if="sharedForAvailableHours && sharedForAvailableHours.length>0" class="col-sm-4">
                    <template v-for="v in sharedForAvailableHours">
                        <div class="form-check">
                            <input v-model="sharedFor" class="form-check-input" type="radio" name="sharedFor" :id="`sharedFor.${v.Value}`" :value="v.Value">
                            <label class="form-check-label" :for="`sharedFor.${v.Value}`">Share for {{v.Name}}</label>
                        </div>
                    </template>
                </div>
                <div v-if="sharedForAvailableDays && sharedForAvailableDays.length>0" class="col-sm-4">
                    <template v-for="v in sharedForAvailableDays">
                        <div class="form-check">
                            <input v-model="sharedFor" class="form-check-input" type="radio" name="sharedFor" :id="`sharedFor.${v.Value}`" :value="v.Value">
                            <label class="form-check-label" :for="`sharedFor.${v.Value}`">Share for {{v.Name}}</label>
                        </div>
                    </template>
                </div>
                <div class="col-sm-4">
                    <div class="form-check">
                        <input v-model="sharedFor" class="form-check-input" type="radio" name="sharedFor" id="sharedFor.forever" :value="null">
                        <label class="form-check-label" for="sharedFor.forever">Share forever</label>
                    </div>
                </div>
            </div>
            <br />

            <a v-if="numberOfShares>0" :href="`/#/sharing/resource/${provider}/${resourceId}`" class="btn btn-lg btn-secondary m-1">Back</a>
            <a v-else :href="resourceUrl" class="btn btn-lg btn-secondary m-1">Back</a>

            <button v-if="exists && !isPaused" @click="invokePauseSharingCommandV1" class="btn btn-lg btn-secondary m-1">Pause sharing</button>
            <button v-if="exists && isPaused" @click="invokeResumeSharingCommandV1" class="btn btn-lg btn-secondary m-1">Resume sharing</button>

            <button @click="invokeShareDataCommandV1" class="btn btn-lg btn-primary pull-right m-1">Share</button>
            <button v-if="exists" @click="handleDeleteShareCommandV1" class="btn btn-lg btn-danger pull-right m-1">Delete share</button>

            <div v-if="authenticatedUserRequired" class="mt-4">
                <strong>
                    To share {{resourceName}} the user must have Business User access to the software.
                </strong>
                <br />
                <span>
                    This should be requested via the <a :href="`/#/pp/people/${workspaceId}/${projectId}`" target="_blank">People</a>
                    section & approved by your workspace admin to enable instant access to this project.
                </span>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, watch, onMounted } from 'vue'
    import { useRoute } from '@common/composables'
    import { useSharePage } from '@DataSharing/Shares/pages-core'
    import '@common/Components/ValidationError.vue'

    document.title = 'Share data'

    const route = useRoute()
    const provider = route.params.provider
    const resourceId = route.params.resourceId
    const shareId = route.params.shareId

    const organizationUnitId = ref()
    const workspaceId = ref()
    const projectId = ref()
    const userId = ref()
    const sharedAt = ref(null)
    const sharedFor = ref()
    const sharedWith = ref()
    const resourceProviderName = ref(provider)
    const emailsProviderName = ref()
    const emailsProviderGroups = ref()
    const emailsProviderConfig = computed(() => emailsProviderGroups.value ? JSON.stringify(emailsProviderGroups.value) : null)
    const exists = ref()
    const isPaused = ref()

    const {
        useDataShareEmailsProvidersQuery, useShareQuery, useEmailsAndGroupsQuery,
        useShareDataCommand, useDeleteShareCommand, usePauseSharingCommand, useResumeSharingCommand
    } = useSharePage()

    const { modelShareQueryV1, runShareQueryV1 } = useShareQuery(resourceProviderName, resourceId, shareId)

    const { modelDataShareEmailsProvidersQueryV1, runDataShareEmailsProvidersQueryV1 } = useDataShareEmailsProvidersQuery(organizationUnitId, workspaceId, projectId)

    const { modelEmailsAndGroupsQueryV1, runEmailsAndGroupsQueryV1 } = useEmailsAndGroupsQuery(organizationUnitId, workspaceId, projectId, emailsProviderName)

    const { invokeShareDataCommandV1, succeedShareDataCommandV1, validationErrors } = useShareDataCommand(
        shareId, organizationUnitId, workspaceId, userId, sharedAt, sharedFor, sharedWith,
        resourceProviderName, resourceId, emailsProviderName, emailsProviderConfig)

    const { invokeDeleteShareCommandV1, succeedDeleteShareCommandV1 } = useDeleteShareCommand(shareId, userId)
    const { invokePauseSharingCommandV1, succeedPauseSharingCommandV1 } = usePauseSharingCommand(shareId)
    const { invokeResumeSharingCommandV1, succeedResumeSharingCommandV1 } = useResumeSharingCommand(shareId)

    function handleDeleteShareCommandV1() {
        if (confirm(`Do you want to delete this share?`)) {
            invokeDeleteShareCommandV1()
        }
    }

    const shares = computed(() => modelShareQueryV1.value?.Shares || [])
    const isShared = computed(() => modelShareQueryV1.value && shares.value.length > 0)
    const newShareId = computed(() => modelShareQueryV1.value?.NewShareId)
    const organizationName = computed(() => modelShareQueryV1.value?.OrganizationUnitName)
    const workspaceName = computed(() => modelShareQueryV1.value?.WorkspaceName)
    const projectName = computed(() => modelShareQueryV1.value?.ProjectName)
    const resourceName = computed(() => modelShareQueryV1.value?.ResourceName)
    const resourceUrl = computed(() => modelShareQueryV1.value?.ResourceUrl)
    const numberOfShares = computed(() => modelShareQueryV1.value?.NumberOfShares)
    const sharedForAvailableHours = computed(() => modelShareQueryV1.value?.SharedForAvailableHours)
    const sharedForAvailableDays = computed(() => modelShareQueryV1.value?.SharedForAvailableDays)
    const authenticatedUserRequired = computed(() => modelShareQueryV1.value?.AuthenticatedUserRequired)

    const emails = computed(() => modelEmailsAndGroupsQueryV1.value?.Emails || [])
    const groups = computed(() => modelEmailsAndGroupsQueryV1.value?.Groups || [])

    const dataShareEmailsProviders = computed(() => modelDataShareEmailsProvidersQueryV1.value?.Providers ?? [])
    const selectedEmailsProvider = computed(() => dataShareEmailsProviders.value.find(_ => _.Name == emailsProviderName.value))

    onMounted(() => runShareQueryV1())

    watch(modelShareQueryV1, () => {
        const model = modelShareQueryV1.value
        organizationUnitId.value = model?.OrganizationUnitId
        workspaceId.value = model?.WorkspaceId
        projectId.value = model?.ProjectId
        userId.value = model?.AuthenticatedUserId
        sharedFor.value = model?.SharedFor;
        emailsProviderName.value = model?.EmailsProviderName
        emailsProviderGroups.value = model?.EmailsProviderGroups || []
        exists.value = model?.Exists
        isPaused.value = model?.IsPaused
        runDataShareEmailsProvidersQueryV1()
    })

    watch(emailsProviderName, val => val ? runEmailsAndGroupsQueryV1() : null)

    watch(succeedShareDataCommandV1, navigateToShareNotificationPageIfSucceed)
    watch(succeedDeleteShareCommandV1, navigateToResourceSharePageIfSucceed)
    watch(succeedPauseSharingCommandV1, navigateToResourceSharePageIfSucceed)
    watch(succeedResumeSharingCommandV1, navigateToResourceSharePageIfSucceed)

    function navigateToResourceSharePageIfSucceed(succeed: boolean) {
        if (succeed) location.href = `/#/sharing/resource/${resourceProviderName.value}/${resourceId}`
    }

    function navigateToShareNotificationPageIfSucceed(succeed: boolean) {
        if (succeed) location.href = `/#/sharing/share/notification/${shareId}`
    }
</script>
